import { useMemo } from 'react';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import type { ServiceProvider } from '@frontend/api-service-providers';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useModalControl } from '@frontend/design-system';
import { useScheduleSettingsSmallMode } from '../../hooks/settings/use-schedule-settings-small-mode';
import { ServiceProviderCompactTable } from './ServiceProviderCompactTable';
import { ServiceProviderLargeTable } from './ServiceProviderLargeTable';
import { ServiceProviderMappingAlertModal } from './ServiceProviderMappingAlertModal';

interface Props {
  providers: ServiceProvider[];
  hasCreateAppointmentCapability?: boolean;
  handleDelete: (id: string, e?: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  handleEdit: (id: string, e?: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  handleEditSchedule: (id: string, e?: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  isLoading: boolean;
  disableProviderActions?: boolean;
}

export function ServiceProviderTable({
  handleEdit,
  handleDelete,
  handleEditSchedule,
  hasCreateAppointmentCapability,
  providers,
  disableProviderActions,
  ...props
}: Props) {
  const { t } = useTranslation('schedule');
  const { data: integrations } = DataSourcesQueries.useGetAccessibleIntegrations();
  const { isSmallMode } = useScheduleSettingsSmallMode();

  const { modalProps, openModal } = useModalControl();

  // Should map to external provider ID if there is Appointment writeback capability because external provider ID is required while writing back to PMS
  const shouldMapToExternalProviderId = hasCreateAppointmentCapability;

  const tableRowActions = useMemo(() => {
    if (disableProviderActions) {
      return [];
    }
    const editProviderIcon = {
      Icon: () => <Icon name='edit' />,
      label: t('Edit Provider'),
      onClick: (row: ServiceProvider) => handleEdit(row.id),
    };
    const editScheduleIcon = {
      Icon: () => <Icon name='calendar' />,
      label: t('Provider Schedule'),
      onClick: (row: ServiceProvider) => handleEditSchedule(row.id),
    };
    const deleteIcon = {
      Icon: () => <Icon name='trash' />,
      label: t('Delete Provider'),
      onClick: (row: ServiceProvider) => handleDelete(row.id),
    };
    return [editProviderIcon, editScheduleIcon, deleteIcon];
  }, [disableProviderActions, handleDelete, handleEdit, handleEditSchedule, t]);

  return (
    <>
      {isSmallMode ? (
        <ServiceProviderCompactTable
          {...props}
          providers={providers}
          tableRowActions={tableRowActions}
          shouldMapToExternalProviderId={shouldMapToExternalProviderId}
          openModal={openModal}
          integrations={integrations}
        />
      ) : (
        <ServiceProviderLargeTable
          {...props}
          providers={providers}
          tableRowActions={tableRowActions}
          shouldMapToExternalProviderId={shouldMapToExternalProviderId}
          openModal={openModal}
          integrations={integrations}
        />
      )}
      <ServiceProviderMappingAlertModal modalProps={modalProps} />
    </>
  );
}
